import React, { useState } from 'react';
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';

interface RoadmapData {
  selectedRecommendation?: {
    name?: string;
    description?: string;
    complexity?: string;
    timeToValue?: string;
  };
  timeline?: any;
  milestones?: any[];
  budgetBreakdown?: any;
  estimatedTotalCost?: number;
  estimatedROI?: string;
  riskAssessment?: string[];
  assessmentId?: string;
  companyName?: string;
  sector?: string;
  aiReadinessScore?: number;
  breakdown?: {
    technology: number;
    dataAnalytics: number;
    organization: number;
    businessCase: number;
  };
}

interface RoadmapPDFExportProps {
  roadmap: RoadmapData;
}

const RoadmapPDFExportV2: React.FC<RoadmapPDFExportProps> = ({ roadmap }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const formatCurrency = (amount: number | undefined) => {
    if (!amount) return 'Da definire';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const generatePDF = async () => {
    setIsGenerating(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPosition = margin;

      // _blank Brand colors
      const primaryBlue = '#001B4C';
      const accentBlue = '#2a69b8';
      const lightBg = '#f0f4fb';
      const textDark = '#495057';

      // Helper function to load and convert SVG to PNG
      const loadLogoImage = (): Promise<string | null> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';

          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = 300;
              canvas.height = 230;
              const ctx = canvas.getContext('2d');

              if (ctx) {
                // Sfondo trasparente
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, 300, 230);
                resolve(canvas.toDataURL('image/png'));
              } else {
                resolve(null);
              }
            } catch (err) {
              console.error('Error converting logo:', err);
              resolve(null);
            }
          };

          img.onerror = () => {
            console.error('Error loading logo');
            resolve(null);
          };

          // Carica il logo dal public folder
          img.src = '/logo_blank_white.svg';

          // Timeout dopo 2 secondi
          setTimeout(() => resolve(null), 2000);
        });
      };

      // Helper function for page breaks
      const checkPageBreak = (neededSpace: number) => {
        if (yPosition + neededSpace > pageHeight - margin - 20) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Add footer function
      const addFooter = () => {
        const footerY = pageHeight - 15;
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text('AI Implementation Advisor powered by _blank growth agency', pageWidth / 2, footerY, { align: 'center' });
        pdf.text('info@blankgrowth.agency | blankgrowth.agency', pageWidth / 2, footerY + 4, { align: 'center' });
      };

      // ============ PAGE 1: HEADER & REPORT ============

      // Header with _blank branding
      pdf.setFillColor(0, 27, 76); // primaryBlue as RGB
      pdf.rect(0, 0, pageWidth, 55, 'F');

      // Load and add _blank logo
      const logoDataUrl = await loadLogoImage();

      if (logoDataUrl) {
        // Aggiungi il logo SVG convertito in PNG
        const logoWidth = 35;
        const logoX = margin;
        pdf.addImage(logoDataUrl, 'PNG', logoX, 15, logoWidth, 27);

        // Agency tagline centrato sotto il logo
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'italic');
        const taglineX = logoX + (logoWidth / 2);
        pdf.text('data driven, human made', taglineX, 44, { align: 'center' });
      } else {
        // Fallback to text se il logo non carica
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(32);
        pdf.setFont('helvetica', 'bold');
        pdf.text('_blank', margin, 28);

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'italic');
        pdf.text('data driven, human made', margin, 36);
      }

      // Document title (centrato a destra)
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI Implementation Report & Roadmap', pageWidth - margin, 28, { align: 'right' });

      yPosition = 65;

      // Company Info Box
      pdf.setFillColor(240, 244, 251);
      pdf.roundedRect(margin, yPosition, contentWidth, 22, 3, 3, 'F');

      pdf.setFontSize(10);
      pdf.setTextColor(textDark);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Azienda:', margin + 5, yPosition + 7);
      pdf.setFont('helvetica', 'normal');
      pdf.text(roadmap.companyName || 'N/A', margin + 25, yPosition + 7);

      pdf.setFont('helvetica', 'bold');
      pdf.text('Settore:', margin + 5, yPosition + 14);
      pdf.setFont('helvetica', 'normal');
      pdf.text(roadmap.sector || 'N/A', margin + 25, yPosition + 14);

      pdf.setFont('helvetica', 'bold');
      pdf.text('Data:', margin + 100, yPosition + 7);
      pdf.setFont('helvetica', 'normal');
      pdf.text(new Date().toLocaleDateString('it-IT'), margin + 115, yPosition + 7);

      yPosition += 30;

      // ============ AI READINESS SCORE SECTION ============
      checkPageBreak(50);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('AI Readiness Assessment', margin, yPosition);
      yPosition += 10;

      // Score Box
      const scoreBoxHeight = 35;
      pdf.setFillColor(accentBlue);
      pdf.roundedRect(margin, yPosition, contentWidth, scoreBoxHeight, 5, 5, 'F');

      pdf.setFontSize(12);
      pdf.setTextColor(255, 255, 255);
      pdf.text('AI Readiness Score', pageWidth / 2, yPosition + 10, { align: 'center' });

      pdf.setFontSize(48);
      pdf.setFont('helvetica', 'bold');
      pdf.text(String(roadmap.aiReadinessScore || 0), pageWidth / 2, yPosition + 27, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('/100', pageWidth / 2 + 15, yPosition + 27);

      yPosition += scoreBoxHeight + 10;

      // Breakdown if available
      if (roadmap.breakdown) {
        checkPageBreak(40);

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(textDark);
        pdf.text('Breakdown Dettagliato', margin, yPosition);
        yPosition += 7;

        const categories = [
          { label: 'Tecnologia', value: roadmap.breakdown.technology },
          { label: 'Data Analytics', value: roadmap.breakdown.dataAnalytics },
          { label: 'Organizzazione', value: roadmap.breakdown.organization },
          { label: 'Business Case', value: roadmap.breakdown.businessCase }
        ];

        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');

        categories.forEach((cat) => {
          checkPageBreak(8);

          // Label
          pdf.text(cat.label, margin + 5, yPosition);

          // Progress bar
          const barWidth = 100;
          const barX = margin + 50;
          pdf.setDrawColor(200, 200, 200);
          pdf.rect(barX, yPosition - 4, barWidth, 5);

          // Fill
          pdf.setFillColor(accentBlue);
          pdf.rect(barX, yPosition - 4, (barWidth * cat.value) / 100, 5, 'F');

          // Score
          pdf.setFont('helvetica', 'bold');
          pdf.text(String(cat.value) + '/100', barX + barWidth + 5, yPosition);
          pdf.setFont('helvetica', 'normal');

          yPosition += 10;
        });

        yPosition += 5;
      }

      // Score Interpretation
      checkPageBreak(30);

      pdf.setFillColor(255, 243, 199);
      pdf.roundedRect(margin, yPosition, contentWidth, 25, 3, 3, 'F');

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor('#92400E');
      pdf.text('Interpretazione', margin + 5, yPosition + 7);

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor('#78350F');

      const score = roadmap.aiReadinessScore || 0;
      let interpretation = '';
      if (score >= 80) {
        interpretation = 'Eccellente! La tua azienda è pronta per implementare soluzioni AI avanzate.';
      } else if (score >= 60) {
        interpretation = 'Buono! La tua azienda ha una base solida ma necessita di alcuni miglioramenti.';
      } else if (score >= 40) {
        interpretation = 'Discreto. Richiede preparazione significativa prima di implementare AI.';
      } else {
        interpretation = 'In sviluppo. Focalizzarsi sul rafforzamento delle fondamenta tecnologiche.';
      }

      const interpLines = pdf.splitTextToSize(interpretation, contentWidth - 10);
      pdf.text(interpLines, margin + 5, yPosition + 14);
      yPosition += 30;

      // ============ RECOMMENDED AI AGENT ============
      checkPageBreak(45);

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('AI Agent Raccomandato', margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(13);
      pdf.setTextColor(accentBlue);
      pdf.text(roadmap.selectedRecommendation?.name || 'N/A', margin, yPosition);
      yPosition += 8;

      if (roadmap.selectedRecommendation?.description) {
        pdf.setFontSize(10);
        pdf.setTextColor(textDark);
        pdf.setFont('helvetica', 'normal');
        const descLines = pdf.splitTextToSize(
          roadmap.selectedRecommendation.description,
          contentWidth
        );
        pdf.text(descLines, margin, yPosition);
        yPosition += descLines.length * 5 + 5;
      }

      // Key Metrics
      checkPageBreak(35);

      pdf.setFillColor(lightBg);
      pdf.roundedRect(margin, yPosition, contentWidth, 30, 3, 3, 'F');

      const metricsY = yPosition + 8;
      const col = contentWidth / 3;

      pdf.setFontSize(9);
      pdf.setTextColor(textDark);
      pdf.setFont('helvetica', 'normal');

      // Budget
      pdf.text('Budget Totale', margin + 5, metricsY);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text(formatCurrency(roadmap.estimatedTotalCost), margin + 5, metricsY + 7);

      // ROI
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(textDark);
      pdf.text('ROI Stimato', margin + col + 5, metricsY);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor('#10B981');
      pdf.text(roadmap.estimatedROI || '200-300%', margin + col + 5, metricsY + 7);

      // Timeline
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(textDark);
      pdf.text('Timeline', margin + col * 2 + 5, metricsY);
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('180 giorni', margin + col * 2 + 5, metricsY + 7);

      yPosition += 38;

      addFooter();

      // ============ PAGE 2: ROADMAP TIMELINE ============
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('Roadmap di Implementazione', margin, yPosition);
      yPosition += 10;

      if (roadmap.timeline) {
        const phases = [
          { key: 'days60', title: roadmap.timeline.days60?.title || 'Fase 1: Fondamenta (0-60 giorni)' },
          { key: 'days120', title: roadmap.timeline.days120?.title || 'Fase 2: Implementazione (60-120 giorni)' },
          { key: 'days180', title: roadmap.timeline.days180?.title || 'Fase 3: Ottimizzazione (120-180 giorni)' }
        ];

        phases.forEach((phase) => {
          checkPageBreak(40);

          // Phase header
          pdf.setFillColor(accentBlue);
          pdf.rect(margin, yPosition, 4, 8, 'F');

          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(primaryBlue);
          pdf.text(phase.title, margin + 7, yPosition + 6);
          yPosition += 12;

          // Tasks
          const tasks = roadmap.timeline[phase.key]?.tasks || [];
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(textDark);

          tasks.slice(0, 5).forEach((task: string) => {
            checkPageBreak(7);
            pdf.text('•', margin + 5, yPosition);
            const taskLines = pdf.splitTextToSize(task, contentWidth - 15);
            pdf.text(taskLines, margin + 10, yPosition);
            yPosition += Math.max(5, taskLines.length * 4.5);
          });

          yPosition += 5;
        });
      }

      addFooter();

      // ============ PAGE 3: BUDGET & RISKS ============
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('Budget Breakdown', margin, yPosition);
      yPosition += 10;

      if (roadmap.budgetBreakdown) {
        pdf.setFontSize(10);
        pdf.setTextColor(textDark);

        Object.entries(roadmap.budgetBreakdown).forEach(([key, value]: [string, any]) => {
          if (value?.amount) {
            checkPageBreak(18);

            // Category name
            pdf.setFont('helvetica', 'bold');
            const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
            pdf.text(label, margin, yPosition);

            // Amount
            pdf.text(formatCurrency(value.amount), pageWidth - margin - 35, yPosition, { align: 'right' });

            yPosition += 5;

            // Description
            if (value.description) {
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor('#6B7280');
              const descLines = pdf.splitTextToSize(value.description, contentWidth - 10);
              pdf.text(descLines.slice(0, 2), margin + 5, yPosition);
              yPosition += Math.min(descLines.length, 2) * 4.5;
              pdf.setTextColor(textDark);
            }

            yPosition += 5;
          }
        });
      }

      // Risk Assessment
      if (roadmap.riskAssessment && roadmap.riskAssessment.length > 0) {
        checkPageBreak(40);
        yPosition += 10;

        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(primaryBlue);
        pdf.text('Risk Assessment', margin, yPosition);
        yPosition += 10;

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(textDark);

        roadmap.riskAssessment.slice(0, 6).forEach((risk: string) => {
          checkPageBreak(8);
          pdf.text('•', margin, yPosition);
          const riskLines = pdf.splitTextToSize(risk, contentWidth - 10);
          pdf.text(riskLines, margin + 7, yPosition);
          yPosition += Math.max(6, riskLines.length * 4.5);
        });
      }

      addFooter();

      // ============ FINAL PAGE: NEXT STEPS & CONTACT ============
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.text('Prossimi Passi', margin, yPosition);
      yPosition += 12;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(textDark);

      const nextSteps = [
        'Rivedi questo report con il tuo team e identifica le priorità',
        'Prenota una call strategica gratuita con i nostri esperti',
        'Inizia con la fase di preparazione secondo la roadmap',
        'Mantieni contatto con noi per supporto e guidance'
      ];

      nextSteps.forEach((step, idx) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${idx + 1}.`, margin, yPosition);
        pdf.setFont('helvetica', 'normal');
        const stepLines = pdf.splitTextToSize(step, contentWidth - 10);
        pdf.text(stepLines, margin + 7, yPosition);
        yPosition += Math.max(8, stepLines.length * 5);
      });

      yPosition += 15;

      // CTA Box with Booking Link
      pdf.setFillColor(accentBlue);
      pdf.roundedRect(margin, yPosition, contentWidth, 35, 5, 5, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Prenota la Tua Call Strategica Gratuita', pageWidth / 2, yPosition + 12, { align: 'center' });

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Parla con un esperto e scopri come implementare AI nel tuo business', pageWidth / 2, yPosition + 20, { align: 'center' });

      // Booking button link
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(primaryBlue);
      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(pageWidth / 2 - 35, yPosition + 24, 70, 8, 2, 2, 'F');
      pdf.textWithLink('PRENOTA ORA', pageWidth / 2, yPosition + 30, {
        align: 'center',
        url: `${window.location.origin}/book-call`
      });

      yPosition += 40;

      // Contact Box
      pdf.setFillColor(primaryBlue);
      pdf.roundedRect(margin, yPosition, contentWidth, 50, 5, 5, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Contattaci', pageWidth / 2, yPosition + 12, { align: 'center' });

      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('_blank growth agency', pageWidth / 2, yPosition + 22, { align: 'center' });

      pdf.setFontSize(10);
      pdf.text('info@blankgrowth.agency', pageWidth / 2, yPosition + 30, { align: 'center' });
      pdf.textWithLink('blankgrowth.agency', pageWidth / 2, yPosition + 37, {
        align: 'center',
        url: 'https://blankgrowth.agency'
      });
      pdf.text('Castellammare di Stabia (NA) | P.IVA 08561731210', pageWidth / 2, yPosition + 44, { align: 'center' });

      // Save PDF
      const fileName = `AI-Report-Roadmap-${roadmap.companyName?.replace(/\s+/g, '-') || 'Company'}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Si è verificato un errore nella generazione del PDF. Riprova.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      onClick={generatePDF}
      disabled={isGenerating}
      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
    >
      <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
      {isGenerating ? 'Generazione...' : 'Esporta Report PDF'}
    </button>
  );
};

export default RoadmapPDFExportV2;
